import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Shield, Radio } from "lucide-react";
import axios from "axios";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const hasTracked = useRef(false);
  const [showEmailEntry, setShowEmailEntry] = useState(false);
  const [otherEmail, setOtherEmail] = useState("");

  // Clear any stale session token when landing on login page
  useEffect(() => {
    if (!user && !loading) {
      localStorage.removeItem('session_token');
    }
  }, [user, loading]);

  // Track landing page visit (only once per page load)
  useEffect(() => {
    if (!hasTracked.current && !user) {
      hasTracked.current = true;
      // Fire and forget - don't wait for response
      axios.post(`${API}/auth/track-landing`, {}, { withCredentials: true }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  // Six taps on the footer reveals the manual-email field. The count resets after a pause, so
  // idle taps spread over time never accumulate into the trigger.
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(tapTimerRef.current), []);

  const revealOnSixTaps = () => {
    if (showEmailEntry) return;
    clearTimeout(tapTimerRef.current);
    tapCountRef.current += 1;
    if (tapCountRef.current >= 6) {
      tapCountRef.current = 0;
      setShowEmailEntry(true);
      return;
    }
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 1500);
  };

  // Sign in as someone other than the phone's Google account.
  //
  // Google has no parameter that forces its blank "Email or phone" form — that only appears when
  // there's no Google session at all (e.g. incognito). With a session it always shows the account
  // chooser. So we take the address here instead and pass it as login_hint, which opens Google's
  // sign-in form with it already filled in.
  //
  // No argument = the original behaviour, byte-for-byte.
  const handleGoogleLogin = (loginHint = "") => {
    // Redirect to our backend's Google OAuth endpoint. Pass our origin so the OAuth flow returns
    // to THIS frontend (supports the TWA's clean domain alongside the web origin).
    const hint = loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : "";
    // OAuth state: a random value kept in this browser and sent along; Google
    // hands it back and App.js only finishes sign-in when it matches — so no
    // one can sign this browser in to THEIR account with a crafted link.
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const state = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    try { localStorage.setItem("oauth_state", JSON.stringify({ v: state, t: Date.now() })); } catch { /* private mode */ }
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(window.location.origin)}${hint}&state=${state}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src="/CHSP_PR_2026.png"
            alt="CHSP"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            className="mx-auto mb-4 h-20 w-auto select-none pointer-events-none"
            style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none", pointerEvents: "none" }}
          />
          <h1 className="text-3xl font-bold tracking-tight text-slate-100 uppercase mb-2">
            CHSP
          </h1>
          <p className="text-slate-400 text-sm uppercase tracking-wider">
            Incident Management System
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-sm p-6">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-slate-200">
                Welcome Back
              </h2>
            </div>

            <Button
              onClick={() => handleGoogleLogin()}
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-bold uppercase tracking-wider rounded-sm"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </Button>

            {/* Escape hatch for anyone whose account isn't the one signed in on the device.
                Deliberately unlabelled — revealed by tapping the footer six times. */}
            {showEmailEntry && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = otherEmail.trim();
                  if (email) handleGoogleLogin(email);
                }}
                className="-mt-3 space-y-2"
              >
                <input
                  type="email"
                  value={otherEmail}
                  onChange={(e) => setOtherEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full h-11 px-3 bg-slate-950 border border-slate-700 rounded-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
                  data-testid="google-login-email-input"
                />
                <Button
                  type="submit"
                  disabled={!otherEmail.trim()}
                  className="w-full h-11 bg-slate-700 hover:bg-slate-600 text-white font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
                  data-testid="google-login-email-continue"
                >
                  Continue
                </Button>
              </form>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-900 px-2 text-slate-500">Secure Access</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Radio className="w-3 h-3" />
                <span>24/7 Operations</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-slate-700"></div>
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                <span>Role-Based Access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer. Six taps here reveals the manual-email sign-in — kept hidden so the normal
            path stays a single button, but reachable without needing an admin on the phone. */}
        <p
          onClick={revealOnSixTaps}
          className="text-center text-xs text-slate-500 mt-6 select-none"
          style={{ WebkitTapHighlightColor: "transparent" }}
          data-testid="login-footer-tap"
        >
          Protected system. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
}
